// 新聞頁 - 新聞內容
'use client';

import { useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom/client';

import NewsSwiper from './NewsSwiper'; // 內頁輪播
import '../../styles/news.scss';

const NewsContent = ({ content, slides = null }) => {
    const contentRef = useRef(null);
    const rootRef = useRef(null);

    // 處理 HTML 內容 加入章節段落
    const processContent = useCallback(() => {
        if (!content || !contentRef.current) return;

        let i = 0;
        const newContent = content
            .replace('<p>@swiper</p>', "<div id='news-swiper'></div>")
            .replace(
                /h2 class="news-title">/g,
                () => `h2 class="news-title"><hr id="paragraph-${i++}">`
            );

        contentRef.current.innerHTML = newContent;
    }, [content]);

    // 處理 iframely
    const processIframely = useCallback(() => {
        if (!('IntersectionObserver' in window)) {
            console.warn('IntersectionObserver not supported');
            return;
        }

        const oembedElements = document.querySelectorAll('oembed[url]');
        if (oembedElements.length === 0) return;

        const observer = new IntersectionObserver(
            async (entries, obs) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        const el = entry.target;
                        if (!el.dataset.iframelyLoaded && window.iframely) {
                            el.dataset.iframelyLoaded = 'true';
                            try {
                                await window.iframely.load(el, el.getAttribute('url'));
                            } catch (error) {
                                console.error('Error loading iframely:', error);
                            }
                        }
                        obs.unobserve(el); // 不再觀察這個元素
                    }
                }
            },
            {
                rootMargin: '100px', // 提前預載
                threshold: 0,
            }
        );

        for (const el of oembedElements) {
            observer.observe(el);
        }

        // 清除觀察器
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        processContent();

        // 等 DOM 更新完再處理 iframely lazy load
        const cleanup = processIframely();
        return () => {
            if (typeof cleanup === 'function') cleanup();
        };
    }, [content, processContent, processIframely]);

    // 觀察器 用於處理 NewsSwiper 的渲染
    useEffect(() => {
        if (slides && slides.length) {
            const container = document.getElementById('news-swiper');

            if (container) {
                // Create the root only once
                if (!rootRef.current) {
                    rootRef.current = ReactDOM.createRoot(container);
                }

                // Render or update the content
                rootRef.current.render(<NewsSwiper slides={slides} />);
            }

            // Setup MutationObserver to listen for changes in the DOM
            const observer = new MutationObserver(() => {
                const container = document.getElementById('news-swiper');
                if (container) {
                    // Update content without creating a new root
                    rootRef.current.render(<NewsSwiper slides={slides} />);
                }
            });

            observer.observe(document.body, { childList: true, subtree: true });

            return () => {
                observer.disconnect();
            };
        }
    }, [slides]);

    // 文章內容
    return (
        <div
            className="news-content [&>*:last-child]:!mb-0 [&_h2>hr]:opacity-0 [&_h2]:relative [&_h2>hr]:absolute [&_h2>hr]:z-[-1] [&_h2>hr]:top-[-70px]
            umd:[&_h2]:!text-h3 umd:[&_h3]:!text-h4 umd:[&_h4]:!text-h5"
            ref={contentRef}
        />
    );
};

export default NewsContent;
